import { Icon, type IconName } from '@/shared/components/ui/icon';
import { cn } from '@/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * Figma 라이브러리 helptext (833:7599) — 아이콘 24 + 14 Medium 안내 문구 · 안쪽 16 · 간격 8 · radius 20
 *
 * tone
 * - primary: State=Default — brand-75 배경 · 파란 글자 · info-circle solid
 * - info: State=Info — 하늘색 배경 · blue-500
 * - error: State=Error — red-50 배경 · 빨간 글자 · warning-circle solid
 * - grey: 화면 오버라이드 (문전약국 835:10408 · 사업자등록증) — grey-50 배경 · 검정 글자
 * - dark: 화면 오버라이드 (로그인 앱 전용 툴팁 864:10504 · GNB Maxmatch 가이드) — 검정 배경 · 흰 글자
 */
const helpTextVariants = cva(
  'flex items-start gap-2 rounded-20 p-4 text-body-14-m',
  {
    variants: {
      tone: {
        primary: 'bg-brand-75 text-fg-accent',
        info: 'bg-[#e3f3ff] text-fg-info',
        error: 'bg-red-50 text-fg-error',
        grey: 'bg-surface-grey text-fg-primary',
        dark: 'bg-grey-900 text-fg-white',
      },
    },
    defaultVariants: { tone: 'primary' },
  },
);

const defaultIcon: Record<
  NonNullable<VariantProps<typeof helpTextVariants>['tone']>,
  IconName
> = {
  primary: 'info-circle-solid-24',
  info: 'info-circle-solid-24',
  error: 'warning-circle-solid-24',
  grey: 'info-circle-solid-24',
  dark: 'info-circle-solid-24',
};

export interface HelpTextProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof helpTextVariants> {
  /** 아이콘 바꾸기. null이면 아이콘 없음 */
  icon?: IconName | null;
}

export const HelpText = ({
  tone,
  icon,
  className,
  children,
  ...props
}: HelpTextProps) => {
  const iconName = icon === undefined ? defaultIcon[tone ?? 'primary'] : icon;
  return (
    <div className={cn(helpTextVariants({ tone }), className)} {...props}>
      {iconName && (
        // dark는 흰 원 안에 검정 기호 (로그인 login-info-white와 같은 모양)
        <Icon
          name={iconName}
          className={cn(
            tone === 'dark' &&
              'text-fg-white [&_path[stroke=white]]:stroke-grey-900',
          )}
        />
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};
